import { useEffect, useState } from 'react';

import { formatCents, type TrialBalanceResponse } from '@nexus/shared';

import { ApiError, apiFetch } from '../../lib/api';
import {
  Alert,
  Card,
  DataTable,
  EmptyState,
  LoadingPage,
  Money,
  Num,
  PageHeader,
  When,
  type Column,
} from '../../ui';

/**
 * The trial balance.
 *
 * ## The whole page is one assertion
 *
 * Every account in the system, its balance, and the sum of all of them — which
 * must be zero. The API answers 500 when it is not, which is why this page
 * reads the response itself rather than going through `apiGet`: a trial
 * balance that does not balance is the most important thing this admin can
 * show, and throwing it away as "request failed" would be exactly the wrong
 * reflex.
 *
 * ## A negative balance is not an error
 *
 * `Client funds received` goes more negative as clients pay in, because it is
 * the outside world's side of the entry; `Paid out to creators` goes positive
 * as money leaves. The two external accounts are what make the middle of the
 * ledger sum to zero, and the page says so rather than colouring a minus sign
 * red.
 */
export function AdminLedger(): React.JSX.Element {
  const [data, setData] = useState<TrialBalanceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const response = await apiFetch('/admin/ledger/trial-balance');
        const body = (await response.json()) as
          | TrialBalanceResponse
          | { error?: { message: string } };
        if ('accounts' in body) setData(body);
        else
          setError(
            (body as { error?: { message: string } }).error?.message ??
              'Could not load the ledger.',
          );
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load the ledger.');
      }
    })();
  }, []);

  if (error) {
    return (
      <>
        <PageHeader eyebrow="Operator" title="Ledger" />
        <Alert tone="critical" title="Could not load the ledger.">
          <span>{error}</span>
        </Alert>
      </>
    );
  }

  if (data === null) return <LoadingPage eyebrow="Operator" title="Ledger" stats />;

  type Account = TrialBalanceResponse['accounts'][number];
  const columns: Array<Column<Account>> = [
    {
      key: 'account',
      header: 'Account',
      primary: true,
      cell: (account) => (
        <>
          {account.accountName}
          <span className="cell-note">{account.accountType.toLowerCase().replace(/_/g, ' ')}</span>
        </>
      ),
    },
    {
      key: 'owner',
      header: 'Owner',
      cell: (account) => account.ownerName ?? account.ownerType.toLowerCase(),
    },
    {
      key: 'entries',
      header: 'Entries',
      numeric: true,
      align: 'end',
      cell: (account) => <Num value={account.entries} />,
    },
    {
      key: 'balance',
      header: 'Balance',
      numeric: true,
      align: 'end',
      cell: (account) => <Money cents={account.balanceCents} />,
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title="Trial balance"
        lead="Every account, its balance, and the sum of all of them. It is zero, or something is wrong with the money."
      />

      {data.balanced ? (
        <Alert tone="positive" title="Balanced.">
          <span>
            <Num value={data.transactions} /> transactions and <Num value={data.entries} />{' '}
            entries, and every account sums to {formatCents(0)}.
          </span>
        </Alert>
      ) : (
        <Alert tone="critical" title="Not balanced.">
          <span>
            The accounts sum to {formatCents(data.totalCents)}. Stop paying anybody and read the
            broken transactions below.
          </span>
        </Alert>
      )}

      {data.unbalancedTransactions.length > 0 && (
        <Card title="Transactions that do not say what they moved">
          <ul className="rows">
            {data.unbalancedTransactions.map((t) => (
              <li key={t.id}>
                <code className="chip quiet">{t.idempotencyKey}</code>
                <span>
                  {t.legs < 2
                    ? `has ${t.legs === 0 ? 'no entries at all' : 'a single entry'}: it moved nothing, and its key now refuses the retry that would have.`
                    : `is out by ${formatCents(t.deltaCents)}`}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card
        title="Accounts"
        sub="A negative balance is normal here: the two external accounts are what make the middle of the ledger sum to zero."
      >
        <DataTable
          columns={columns}
          rows={data.accounts}
          getKey={(account) => account.id}
          empty={
            <EmptyState title="No accounts yet.">
              The first funded campaign opens the first accounts.
            </EmptyState>
          }
          footer={
            <tr>
              <th colSpan={3}>Total</th>
              <th data-align="end">
                <Money cents={data.totalCents} />
              </th>
            </tr>
          }
        />
        <p className="muted">
          Read <When value={data.generatedAt} withTime />.
        </p>
      </Card>
    </>
  );
}
